import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';

import { UserModel } from '../../viewmodels/user.model';
import { AdminService } from '../../../../layouts/services/admin.service';
import { Global } from '../../../../app.global';
import { UserSettingModel } from '../../viewmodels/user-setting.model';
import { SettingsService } from '../../../../authentication/services/settings.service';
import { CreateUserViewModel } from '../../viewmodels/create-user-viewmodel';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
})
export class AddUserComponent implements OnInit {
  @ViewChild('userForm') userForm: NgForm;

  // appSettings = this.global.appSettings;
  passwordRegex = '';
  passwordValidationError = '';
  addEditPageTitle = 'add user';
  isEditPage = false;
  userId = 0;
  user: UserModel = new UserModel();
  userSettings: UserSettingModel = new UserSettingModel();
  teamLeaders = [];
  validUsernameDomains: string[] = [];
  originalUsername: string = null;
  errorStatus = false;
  errorMessage = '';
  successMessage = '';
  resendDialogOpen = false;
  unlockDialogOpen = false;
  isProcessingAccountAction = false;
  submitAttempted = false;
  signatures = [];
  clientDomain = 'user@clientdomain.com'; // todo: make this dynamic vs the currently logged in client
  isSaving = false;
  requirePassword = true;
  urlRoot = '';

  constructor(
    private route: ActivatedRoute,
    private adminService: AdminService,
    private settingsService: SettingsService,
    private router: Router,
    private global: Global
  ) {
    this.route.params.subscribe((params) => {
      if (params.id !== undefined) {
        this.isEditPage = true;
        // Route params are always strings; userId is declared/used as a
        // number, and previously that was invisible since it was only ever
        // string-concatenated into URL paths. Coercing here so it's a real
        // number wherever it's used, including the new JSON.stringify(userId)
        // calls (resend/unlock), where a stray string would otherwise be
        // sent as a quoted JSON string instead of a number.
        this.userId = Number(params.id);
      } else {
        this.isEditPage = false;
      }
    });

    this.requirePassword = !this.isEditPage;
    this.clientDomain = 'user@' + this.global.clientDomain;
  }

  ngOnInit() {
    this.urlRoot = window.location.origin;

    if (this.isEditPage) {
      this.addEditPageTitle = 'edit user';
      this.setUser();
      this.setUserSettings();
    }
    // this.passwordValidationError = this.appSettings['Password.ComplexityValidationMessage'];
    this.settingsService.getApplicationSetting('Password.ComplexityValidationMessage').subscribe(
      (result) => {
        this.passwordValidationError = result;
      },
      (err) => {
        this.passwordValidationError = '';
      }
    );
    this.settingsService.getApplicationSetting('Password.ComplexityRegex').subscribe(
      (result) => {
        this.passwordRegex = result;
      },
      (err) => {
        this.passwordRegex = '';
      }
    );
    this.getTeamLeaders();
    this.getDefaultSignatures();
    this.getValidUsernameDomains();
  }

  setUser() {
    this.adminService.getResponseMasterUserById(this.userId).subscribe((result) => {
      if (result) {
        this.user = result;
        this.user.password = '';
        this.user.confirmPassword = '';
        this.originalUsername = result.username;
        this.setUserChangeDecision();
      }
    });
  }

  setUserSettings() {
    this.adminService.getUserSettingById(this.userId).subscribe((result) => {
      this.userSettings = result;
    });
  }

  setUserChangeDecision() {
    this.user.changeDecision = this.user.editLetter ? '1' : '0';
  }

  openResendDialog() {
    this.resendDialogOpen = true;
  }

  closeResendDialog() {
    this.resendDialogOpen = false;
  }

  confirmResendActivationEmail() {
    // Guards against a fast double-click on "Yes" firing two requests -
    // the resend endpoint isn't transactional, so two near-simultaneous
    // calls could both create a live token instead of the second
    // superseding the first.
    if (this.isProcessingAccountAction) {
      return;
    }
    this.isProcessingAccountAction = true;
    this.closeResendDialog();
    this.errorStatus = false;
    this.successMessage = '';

    this.adminService.resendActivationEmail(this.userId, this.urlRoot).subscribe(
      (result) => {
        if (result) {
          this.successMessage =
            'A new activation email has been sent. The previous activation link no longer works.';
        } else {
          this.errorStatus = true;
          this.errorMessage =
            'This user has already activated their account, so no email was sent.';
          this.user.isActivated = true;
        }
        this.isProcessingAccountAction = false;
        setTimeout(() => this.scrollToFormMessages());
      },
      (error) => {
        this.errorStatus = true;
        this.errorMessage = 'Something went wrong! Please try again.';
        this.isProcessingAccountAction = false;
        setTimeout(() => this.scrollToFormMessages());
      }
    );
  }

  openUnlockDialog() {
    this.unlockDialogOpen = true;
  }

  closeUnlockDialog() {
    this.unlockDialogOpen = false;
  }

  confirmUnlockAccount() {
    if (this.isProcessingAccountAction) {
      return;
    }
    this.isProcessingAccountAction = true;
    this.closeUnlockDialog();
    this.errorStatus = false;
    this.successMessage = '';

    this.adminService.unlockUserAccount(this.userId).subscribe(
      (result) => {
        if (result) {
          this.user.isAccountLocked = false;
          this.successMessage = 'The account has been unlocked.';
        } else {
          this.errorStatus = true;
          this.errorMessage = 'Something went wrong! Please try again.';
        }
        this.isProcessingAccountAction = false;
        setTimeout(() => this.scrollToFormMessages());
      },
      (error) => {
        this.errorStatus = true;
        this.errorMessage = 'Something went wrong! Please try again.';
        this.isProcessingAccountAction = false;
        setTimeout(() => this.scrollToFormMessages());
      }
    );
  }

  getTeamLeaders() {
    this.adminService.getTeamLeaders().subscribe((result) => {
      if (result) {
        this.teamLeaders = result;
      }
    });
  }

  getValidUsernameDomains() {
    this.adminService.getValidUsernameDomains().subscribe(
      (result) => {
        this.validUsernameDomains = result || [];
      },
      (error) => {
        // Leave validUsernameDomains empty - isUsernameDomainValid() treats
        // an empty/unloaded list as "can't check yet" rather than blocking
        // the user on our own failed request. The backend enforces this
        // regardless, so this is UX only.
        this.validUsernameDomains = [];
      }
    );
  }

  isUsernameDomainValid(): boolean {
    if (!this.user.username || this.validUsernameDomains.length === 0) {
      return true;
    }
    // Editing a user without changing their username shouldn't be blocked
    // by a domain that predates today's valid-domains configuration.
    if (
      this.isEditPage &&
      this.originalUsername &&
      this.user.username.toLowerCase() === this.originalUsername.toLowerCase()
    ) {
      return true;
    }
    const domain = this.user.username.split('@')[1];
    if (!domain) {
      return true; // let `required`/type=email handle malformed input
    }
    return this.validUsernameDomains.some((d) => d.toLowerCase() === domain.toLowerCase());
  }

  usernameDomainErrorMessage(): string {
    const domains = this.validUsernameDomains.join(' or ');
    return (
      `This user name's domain doesn't match ${domains}. RM uses the domain in the user name to identify ` +
      `your organisation, so it must end in @${domains}. The activation email will still go to whatever ` +
      `address you enter in contact email address below — you don't need to change that.`
    );
  }

  onSaveClick() {
    this.submitAttempted = true;
    if (!this.userForm.form.valid || !this.isUsernameDomainValid()) {
      this.userForm.form.markAllAsTouched();
      // A stale green success message from an earlier resend/unlock action
      // shouldn't linger next to (or flash over) a new red validation error.
      this.successMessage = '';
      // Wait for Angular to render the now-visible messages first - scrolling
      // against the pre-error layout (wrong target height/position) is what
      // caused the jump/judder rather than a clean animation.
      setTimeout(() => this.scrollToFormMessages());
    }
  }

  scrollToFormMessages() {
    const messages = document.getElementById('userFormMessages');
    if (!messages) {
      return;
    }

    // The admin panel scrolls inside `.panel-container` (see
    // src/app/style/admin-layout.scss - height: 75vh; overflow-y: auto),
    // not the window/page itself, so the offset has to be applied to that
    // container's scrollTop, not window.scrollY.
    const container = messages.closest('.panel-container') as HTMLElement;
    const topOffset = 100; // keeps the "add/edit user" title visible above the messages

    if (container) {
      const top =
        messages.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop -
        topOffset;
      container.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    } else {
      messages.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // preventScroll stops focus() triggering its own instant jump, which
    // was fighting the smooth scroll above.
    messages.focus({ preventScroll: true });

    // Restart the flash even if it's already showing (e.g. a second failed
    // save straight after the first) - removing then re-adding the class
    // forces the browser to replay the animation instead of a no-op.
    // Colour follows whichever message is actually showing. Keyed off
    // successMessage rather than errorStatus - onSaveClick()'s client-side
    // validation failures (required fields, username domain) show a red
    // message without ever touching errorStatus, so errorStatus alone
    // would default those to the green flash.
    const flashClass = this.successMessage ? 'flash-success' : 'flash';
    messages.classList.remove('flash', 'flash-success');
    void messages.offsetWidth;
    messages.classList.add(flashClass);
  }

  getDefaultSignatures() {
    this.adminService.getDefaultSignatures().subscribe(
      (result) => {
        this.signatures = result;
      },
      (error) => {
        this.signatures = [];
        console.log(error);
      }
    );
  }

  setUserEditLetter() {
    this.user.editLetter = this.user.changeDecision === '1';
  }

  onChangeRole(roleId) {
    if (roleId === '3') {
      this.user.teamLeader = 0;
      this.user.adminAccess = true;
      this.user.reportsAccess = true;
      this.user.editLetter = true;
      this.user.deleted = false;

      this.setUserChangeDecision();
    }
  }

  onSubmitUserForm() {
    this.isSaving = true;
    this.errorStatus = false;
    this.errorMessage = '';
    this.successMessage = '';

    if (this.user.id === 1) {
      return;
    }

    if (!this.isUsernameDomainValid()) {
      // Message is already shown via the real-time field check in the
      // template (onSaveClick marks the field touched) - don't also set
      // errorStatus/errorMessage here, or the same message shows twice.
      this.isSaving = false;
      return;
    }
    // if (!this.isPasswordConfirmed()) {
    //   console.log("passwords don't match");
    //   return;
    // }

    // update user editLetter setting
    this.setUserEditLetter();

    if (this.isEditPage) {
      this.adminService.update(this.user).subscribe(
        (result) => {
          if (result) {
            this.userSettings.responseMasterUserId = this.userId;
            if (this.userSettings.dailyLetterTarget === null) {
              this.userSettings.dailyLetterTarget = 0;
            }
            this.adminService.updateUserSetting(this.userSettings).subscribe(() => {
              this.router.navigate(['/admin/users'], { queryParams: { action: 'edit' } });
            });
          } else {
            this.errorStatus = true;
            setTimeout(() => this.scrollToFormMessages());
          }
          this.isSaving = false;
        },
        (error) => {
          this.errorStatus = true;

          if (error?.error?.message) {
            this.errorMessage = error.error.message;
          } else if (typeof error?.error === 'string') {
            this.errorMessage = error.error;
          } else {
            this.errorMessage = 'Something went wrong! Please try again.';
          }

          this.isSaving = false;
          setTimeout(() => this.scrollToFormMessages());
        }
      );
    } else {
      this.setNewUserData();
      const createUserVM = new CreateUserViewModel();
      createUserVM.user = this.user;
      createUserVM.requestOrigin = this.urlRoot;
      this.adminService.create(createUserVM).subscribe(
        (createdUserViewModel) => {
          if (createdUserViewModel.successful) {
            this.userSettings.responseMasterUserId = createdUserViewModel.userId;
            if (this.userSettings.dailyLetterTarget === null) {
              this.userSettings.dailyLetterTarget = 0;
            }
            this.adminService.updateUserSetting(this.userSettings).subscribe((result) => {
              this.router.navigate(['/admin/users'], { queryParams: { action: 'add' } });
            });
          } else {
            this.errorStatus = true;
            if (createdUserViewModel.message !== '') {
              this.errorMessage = createdUserViewModel.message;
            } else {
              this.errorMessage = 'Something went wrong! Please try again.';
            }
            setTimeout(() => this.scrollToFormMessages());
          }
          this.isSaving = false;
        },
        (error) => {
          this.errorStatus = true;
          this.errorMessage = 'Something went wrong! Please try again.';
          this.isSaving = false;
          setTimeout(() => this.scrollToFormMessages());
        }
      );
    }
  }

  isPasswordValid() {
    if (this.user.password === '') {
      return true;
    }
    return this.passwordRegex && this.user.password.match(this.passwordRegex) !== null;
  }

  setNewUserData() {
    this.user.roleId = parseInt(this.user.roleId.toString(), 10);
    this.user.role.id = this.user.roleId;
    this.user.role.name =
      this.user.role.id === 1 ? 'User' : this.user.role.id === 2 ? 'Team leader' : 'Manager';
    // this.setUserEditLetter();
  }

  isPasswordConfirmed() {
    return this.user.password === this.user.confirmPassword;
  }
}
